import React, { useState } from "react"
import type { Technology } from "~/react/types/discover"
import { CollapsableFilter } from "./collapsable_filter"
import { FilterSearchInput } from "./filter_search_input"
import { FilterDropdown, FilterDropdownGroup, FilterDropdownOption } from "./filter_dropdown"
import { IncludeExcludeTag } from "./include_exclude_tag"
import { useDisplayOrder, toggleIncludeExclude, removeFromBoth } from "./filter-utils"

const t = (key: string) => window.i18n.t(key)

interface TechnologiesFilterProps {
  includedValues: string[]
  excludedValues: string[]
  operator: "and" | "or"
  technologies: Record<string, Technology[]>
  onChangeIncluded: (values: string[]) => void
  onChangeExcluded: (values: string[]) => void
  onChangeOperator: (operator: "and" | "or") => void
  isLocked?: boolean
}

export function TechnologiesFilter({
  includedValues = [],
  excludedValues = [],
  operator = "and",
  technologies = {},
  onChangeIncluded,
  onChangeExcluded,
  onChangeOperator,
  isLocked = false,
}: TechnologiesFilterProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)

  // Flatten technologies for easier searching
  const allTechnologies = Object.values(technologies).flat()
  const allSelectedSet = new Set([...includedValues, ...excludedValues])
  const hasValues = allSelectedSet.size > 0
  const hasMultipleValues = allSelectedSet.size > 1
  const allExcluded = excludedValues.length > 0 && includedValues.length === 0

  // Use shared hook for stable display order
  const orderedSelectedSlugs = useDisplayOrder(includedValues, excludedValues)

  // Filter technologies based on search term and exclude already selected
  const filteredTechnologies = allTechnologies.filter((tech) => {
    const matchesSearch = tech.display_name.toLowerCase().includes(searchTerm.toLowerCase())
    const notSelected = !allSelectedSet.has(tech.slug)
    return matchesSearch && notSelected
  })

  // Group filtered technologies by category
  const groupedFiltered = filteredTechnologies.reduce(
    (acc, tech) => {
      if (!acc[tech.category]) {
        acc[tech.category] = []
      }
      acc[tech.category].push(tech)
      return acc
    },
    {} as Record<string, Technology[]>
  )

  const getCategoryLabel = (category: string) => {
    // Convert snake_case to Title Case
    return category
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const getTechnologyBySlug = (slug: string): Technology | undefined => {
    return allTechnologies.find((tech) => tech.slug === slug)
  }

  const handleSelectTechnology = (slug: string) => {
    onChangeIncluded([...includedValues, slug])
    setSearchTerm("")
    setShowDropdown(false)
  }

  const handleRemoveTechnology = (slug: string) => {
    removeFromBoth(slug, includedValues, excludedValues, onChangeIncluded, onChangeExcluded)
  }

  const handleToggleIncludeExclude = (slug: string) => {
    toggleIncludeExclude(slug, includedValues, excludedValues, onChangeIncluded, onChangeExcluded)
  }

  const handleClear = () => {
    onChangeIncluded([])
    onChangeExcluded([])
    setSearchTerm("")
  }

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.technologies.title")}
      icon="microchip"
      tooltip={t("js.back.discover.filters.technologies.tooltip")}
      count={allSelectedSet.size}
      hasValues={hasValues}
      onClear={handleClear}
      isLocked={isLocked}
      lockedTooltip={t("js.back.discover.filters.technologies.locked_popover")}
    >
      <FilterSearchInput
        id="technologies-search"
        label={t("js.back.discover.filters.technologies.title")}
        placeholder={t("js.back.discover.filters.technologies.placeholder")}
        value={searchTerm}
        onChange={(value) => {
          setSearchTerm(value)
          setShowDropdown(true)
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setShowDropdown(false)}
      />

      <FilterDropdown show={showDropdown && Object.keys(groupedFiltered).length > 0}>
        {Object.entries(groupedFiltered)
          .sort(([catA], [catB]) => catA.localeCompare(catB))
          .map(([category, techs]) => (
            <FilterDropdownGroup key={category} header={getCategoryLabel(category)}>
              {techs.map((tech) => (
                <FilterDropdownOption
                  key={tech.slug}
                  value={tech.slug}
                  onSelect={handleSelectTechnology}
                >
                  {tech.display_name}
                </FilterDropdownOption>
              ))}
            </FilterDropdownGroup>
          ))}
      </FilterDropdown>

      {hasMultipleValues && (
        <nav className={`h-pill-tabs h-pill-tabs--sm h-pill-tabs--full mt-1${allExcluded ? " disabled" : ""}`}>
          <button
            type="button"
            className={`h-pill-tabs__link${operator === "and" ? " is-active" : ""}`}
            onClick={() => !allExcluded && onChangeOperator("and")}
            disabled={allExcluded}
            data-controller="tooltip"
            data-bs-trigger="hover"
            title={t("js.back.discover.filters.match_all_tooltip")}
          >
            <span className="h-pill-tabs__name">{t("js.back.discover.filters.match_all")}</span>
          </button>
          <button
            type="button"
            className={`h-pill-tabs__link${operator === "or" ? " is-active" : ""}`}
            onClick={() => !allExcluded && onChangeOperator("or")}
            disabled={allExcluded}
            data-controller="tooltip"
            data-bs-trigger="hover"
            title={t("js.back.discover.filters.match_any_tooltip")}
          >
            <span className="h-pill-tabs__name">{t("js.back.discover.filters.match_any")}</span>
          </button>
        </nav>
      )}

      {hasValues && (
        <div className="mt-2">
          {orderedSelectedSlugs.map((slug) => {
            const tech = getTechnologyBySlug(slug)
            if (!tech) return null

            return (
              <IncludeExcludeTag
                key={slug}
                label={tech.display_name}
                isIncluded={includedValues.includes(slug)}
                onToggle={() => handleToggleIncludeExclude(slug)}
                onRemove={() => handleRemoveTechnology(slug)}
              />
            )
          })}
        </div>
      )}
    </CollapsableFilter>
  )
}
